import { useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Save, Loader2, Target, ArrowLeft, Share2, FileText, FileDown, Crown, Lock, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { PersonalInfoSection, WorkExperienceSection, EducationSection, ExtrasSection } from '@/components/ResumeFormSections';
import { ResumePreview } from '@/components/ResumePreview';
import { AIDialog, ATSDialog } from '@/components/BuilderDialogs';
import { useAutoSave } from '@/hooks/useAutoSave';
import { useResumeData } from '@/hooks/useResumeData';
import { useAIAssistant } from '@/hooks/useAIAssistant';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { ThemeToggle } from '@/components/ThemeToggle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResumeBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const resume = useResumeData(id, isNew, navigate);
  const ai = useAIAssistant(resume.setWorkExperience, resume.setProfessionalSummary);
  const { autoSaveStatus, triggerAutoSave } = useAutoSave(resume.resumeId, resume.dataLoaded, resume.buildResumePayload);
  const { isPro } = useSubscription();

  useEffect(() => {
    triggerAutoSave();
  }, [resume.personalInfo, resume.professionalSummary, resume.workExperience, resume.education, resume.skills, resume.projects, resume.certifications, resume.title, resume.template, triggerAutoSave]);

  const openSummaryAI = useCallback(() => {
    ai.openSummaryAI({ workExperience: resume.workExperience, education: resume.education, skills: resume.skills });
  }, [ai, resume.workExperience, resume.education, resume.skills]);

  const openBulletAI = useCallback((workIndex) => {
    ai.openBulletAI(workIndex, resume.workExperience[workIndex]);
  }, [ai, resume.workExperience]);

  const handleATSCheck = useCallback(() => {
    ai.handleATSCheck({
      personal_info: resume.personalInfo, professional_summary: resume.professionalSummary,
      work_experience: resume.workExperience, education: resume.education,
      skills: resume.skills, projects: resume.projects, certifications: resume.certifications,
    });
  }, [ai, resume.personalInfo, resume.professionalSummary, resume.workExperience, resume.education, resume.skills, resume.projects, resume.certifications]);

  const handleExportPDF = useCallback(() => {
    if (!resume.resumeId) { toast.error('Save your resume first'); return; }
    if (!isPro) { toast.error('Upgrade to Pro to download PDF'); navigate('/subscription'); return; }
    window.open(`${API_URL}/api/resumes/${resume.resumeId}/export/pdf`, '_blank');
  }, [resume.resumeId, isPro, navigate]);

  const handleExportDOCX = useCallback(() => {
    if (!resume.resumeId) { toast.error('Save your resume first'); return; }
    if (!isPro) { toast.error('Upgrade to Pro to download DOCX'); navigate('/subscription'); return; }
    window.open(`${API_URL}/api/resumes/${resume.resumeId}/export/docx`, '_blank');
  }, [resume.resumeId, isPro, navigate]);

  const handleShare = useCallback(() => {
    if (!resume.shareToken) { toast.error('Save your resume first'); return; }
    const shareUrl = `${window.location.origin}/shared/${resume.shareToken}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      toast.success('Share link copied to clipboard!');
    }).catch(() => {
      toast.info(`Share link: ${shareUrl}`);
    });
  }, [resume.shareToken]);

  return (
    <div className="h-screen w-full flex overflow-hidden bg-slate-50 dark:bg-slate-900">
      {/* Left Panel - Form */}
      <div className="w-7/12 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 p-4 md:p-6">
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center gap-3">
              <Button data-testid="back-to-dashboard-btn" onClick={() => navigate('/dashboard')} className="bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 rounded-none px-3 py-2 text-sm">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Input data-testid="resume-title-input" value={resume.title} onChange={(e) => resume.setTitle(e.target.value)} className="text-xl font-bold border-none focus:ring-0 px-0 max-w-xs dark:bg-transparent dark:text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }} />
            </div>
            <div className="flex items-center gap-2">
              <div className="px-2 py-1 bg-[#FFEDD5] dark:bg-orange-900 border border-[#FB923C] text-slate-900 dark:text-orange-200 text-xs font-bold">{resume.completenessScore()}%</div>
              <div data-testid="auto-save-status" className="text-xs px-2 py-1 border border-slate-200 dark:border-slate-600 hidden md:block">
                {autoSaveStatus === 'saving' && <span className="text-amber-600 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving...</span>}
                {autoSaveStatus === 'saved' && <span className="text-emerald-600 dark:text-emerald-400">Saved</span>}
                {autoSaveStatus === 'unsaved' && <span className="text-slate-500">Unsaved</span>}
                {autoSaveStatus === 'error' && <span className="text-red-500">Error</span>}
              </div>
              <ThemeToggle />
              <Button data-testid="save-resume-btn" onClick={resume.saveResume} disabled={resume.saving} className="bg-[#FB923C] hover:bg-orange-300 text-slate-900 font-bold rounded-none px-4 py-2 text-sm transition-all">
                {resume.saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save
              </Button>
            </div>
          </div>
          <div className="flex gap-3 items-center flex-wrap">
            <Select value={resume.template} onValueChange={resume.setTemplate}>
              <SelectTrigger data-testid="template-selector" className="w-36 rounded-none border-slate-200 dark:border-slate-600 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="modern">Modern</SelectItem>
                <SelectItem value="classic">Classic</SelectItem>
                <SelectItem value="minimal">Minimal</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="ats-check-btn" onClick={() => ai.setAtsDialogOpen(true)} className="bg-white dark:bg-slate-700 border-2 border-slate-900 dark:border-slate-500 text-slate-900 dark:text-slate-100 font-bold rounded-none px-3 py-2 text-sm transition-all hover:bg-slate-50 shadow-[2px_2px_0px_#0F172A] dark:shadow-none">
              <Target className="w-4 h-4 mr-1" /> ATS
            </Button>
            <Button data-testid="ats-optimize-btn" onClick={() => navigate(`/ats-optimizer/${resume.resumeId || 'new'}`)} className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold rounded-none px-3 py-2 text-sm transition-all shadow-[2px_2px_0px_#0F172A]">
              <Zap className="w-4 h-4 mr-1" /> Optimize
            </Button>
            <Button data-testid="export-pdf-btn" onClick={handleExportPDF} className="bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 rounded-none px-3 py-2 text-sm">
              {isPro ? <FileText className="w-4 h-4 mr-1" /> : <Lock className="w-4 h-4 mr-1" />} PDF
            </Button>
            <Button data-testid="export-docx-btn" onClick={handleExportDOCX} className="bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 rounded-none px-3 py-2 text-sm">
              {isPro ? <FileDown className="w-4 h-4 mr-1" /> : <Lock className="w-4 h-4 mr-1" />} DOCX
            </Button>
            <Button data-testid="share-btn" onClick={handleShare} className="bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 rounded-none px-3 py-2 text-sm">
              <Share2 className="w-4 h-4 mr-1" /> Share
            </Button>
          </div>
        </div>

        <div className="p-6">
          <Tabs defaultValue="personal" className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-6">
              <TabsTrigger value="personal" data-testid="tab-personal">Personal</TabsTrigger>
              <TabsTrigger value="experience" data-testid="tab-experience">Experience</TabsTrigger>
              <TabsTrigger value="education" data-testid="tab-education">Education</TabsTrigger>
              <TabsTrigger value="extras" data-testid="tab-extras">Extras</TabsTrigger>
            </TabsList>
            <TabsContent value="personal">
              <PersonalInfoSection personalInfo={resume.personalInfo} setPersonalInfo={resume.setPersonalInfo} professionalSummary={resume.professionalSummary} setProfessionalSummary={resume.setProfessionalSummary} onOpenAI={openSummaryAI} />
            </TabsContent>
            <TabsContent value="experience">
              <WorkExperienceSection workExperience={resume.workExperience} setWorkExperience={resume.setWorkExperience} onOpenAI={openBulletAI} />
            </TabsContent>
            <TabsContent value="education">
              <EducationSection education={resume.education} setEducation={resume.setEducation} />
            </TabsContent>
            <TabsContent value="extras">
              <ExtrasSection skills={resume.skills} setSkills={resume.setSkills} projects={resume.projects} setProjects={resume.setProjects} certifications={resume.certifications} setCertifications={resume.setCertifications} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Right Panel - Live Preview */}
      <div className="w-5/12 bg-slate-100 dark:bg-slate-950 p-8 overflow-y-auto flex justify-center">
        <div className="w-full max-w-3xl">
          <ResumePreview template={resume.template} personalInfo={resume.personalInfo} professionalSummary={resume.professionalSummary} workExperience={resume.workExperience} education={resume.education} skills={resume.skills} projects={resume.projects} certifications={resume.certifications} />
        </div>
      </div>

      <AIDialog open={ai.aiDialogOpen} onOpenChange={ai.setAiDialogOpen} prompt={ai.aiPrompt} onPromptChange={ai.setAiPrompt} loading={ai.aiLoading} onGenerate={ai.handleAIGenerate} />
      <ATSDialog open={ai.atsDialogOpen} onOpenChange={ai.setAtsDialogOpen} jobDescription={ai.jobDescription} onJobDescriptionChange={ai.setJobDescription} loading={ai.atsLoading} onCheck={handleATSCheck} result={ai.atsResult} />
    </div>
  );
}
